import React, { useState, forwardRef, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Grid,
  Typography,
  Stack,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Drawer,
  TableRow,
  TableCell,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  Checkbox,
  Card,
  AccordionActions,
  Chip,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import Iconify from '../common/iconify.component';
import SnackbarInfo from '../common/snackbar-info.component';
import { getListData } from '../../services/videos.service';
import StyledDialogActions from '../../theme/dialogActionStyles';
import ListVideosData from '../table-elements/list-videos-data.component';
import Workspace from '../form-elements/workspace.component';
import FilterVideos from '../filter-videos/filter-video.component';
import prepareDynamicColumns, {
  getEditableCellDefaultValue,
} from '../../utils/dynamic-columns.util';
import {
  DEFAULT_LIST_COLUMNS,
  DYNAMIC_COLUMNS_CONFIG,
} from '../../config/module-configs/content-videos.config';
import EditableCellValue from '../table-elements/editable-cell-value.component';
import useAccess from '../../hooks/access.hook';
import FieldChooser from '../form-elements/field-chooser.component';
import DistributorFilterVideos from '../filter-videos/distributor-filter-videos.component';
import useAbortableRequest from '../../hooks/abortable-request.hook';
import useUnsavedChanges from '../../hooks/use-unsaved-changes.hook';
import ConfirmPopup from '../common/confirm-popup.component';
import { ACTIONS } from '../../config/const.config';
import replaceIdUrl from '../../utils/url.util';
import ROUTE_PATH from '../../config/routes.config';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const EditVideosList = ({
  title,
  onCancel,
  viewModeWorkspace,
  videoListFilters,
  initialColumnFields,
  isEditMode,
}) => {
  const { getSignal, isAbortError } = useAbortableRequest();
  const { hasOperationAccess, hasContentOwnerAccess } = useAccess();
  const { control, handleSubmit, getValues } = useForm();
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });

  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 50,
    totalRows: 0,
    rows: [],
    columns: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    appliedFilters: videoListFilters,
    filtersCounter: 0,
  });
  const [selectedWorkspace, setSelectedWorkspace] = useState(viewModeWorkspace);
  const [expandBulkEdit, setExpandBulkEdit] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [columnFields, setColumnFields] = useState(initialColumnFields);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showApplyFilterConfirmDialog, setShowApplyFilterConfirmDialog] =
    useState(false);
  const childRef = useRef();
  const bulkEditInitialValues = useRef({});
  const selectedFiltersRef = useRef(options.appliedFilters);

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const checkUnsavedChanges = () => {
    const currentBulkEditValues = getValues();
    const initialBulkEditValues = bulkEditInitialValues.current;

    const hasBulkEditChanges = Object.keys(currentBulkEditValues).some(
      (key) => {
        const currentValue = currentBulkEditValues[key];
        const initialValue = initialBulkEditValues[key];

        if (typeof currentValue === 'object' && currentValue !== null) {
          return JSON.stringify(currentValue) !== JSON.stringify(initialValue);
        }

        return currentValue !== initialValue;
      }
    );

    const hasInlineEditChanges =
      childRef.current?.hasUnsavedChanges?.() || false;

    return hasBulkEditChanges || hasInlineEditChanges;
  };

  const handleApplyFilters = (selectedFilters) => {
    const counter =
      Object.values(selectedFilters).filter((filter) => filter.apply).length ||
      0;

    setOptions({
      ...options,
      appliedFilters: { ...selectedFilters },
      filtersCounter: counter,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleFilterApply = (selectedFilters) => {
    selectedFiltersRef.current = selectedFilters;
    if (checkUnsavedChanges()) {
      setShowApplyFilterConfirmDialog(true);
    } else {
      handleApplyFilters(selectedFilters);
    }
  };

  const handleApplyFilterConfirmSuccess = () => {
    setShowApplyFilterConfirmDialog(false);
    handleApplyFilters(selectedFiltersRef.current);
  };

  const handleApplyFilterConfirmCancel = () => {
    setShowApplyFilterConfirmDialog(false);
  };

  const handleClearFilters = () => {
    setOptions({
      ...options,
      appliedFilters: {
        licenese_expired_videos: {
          apply: true,
          condition: 'EQUALS_TO',
          title: 'Show License Expired Videos',
          type: 'checkbox',
          value: false,
        },
      },
      filtersCounter: 0,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleDeleteFilters = (field) => {
    if (field === 'clear-all') {
      handleClearFilters();
      return;
    }

    const updatedFilters = { ...options.appliedFilters };
    if (field === 'licenese_expired_videos') {
      updatedFilters[field] = { ...updatedFilters[field], value: false };
    } else {
      delete updatedFilters[field];
    }

    setOptions({
      ...options,
      appliedFilters: updatedFilters,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const onFieldClick = (fields) => {
    setColumnFields(fields);
    handleRefreshData();
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    if (!selectedColumns.length || !selectedRows.length) {
      setTimeout(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'info',
          message: !selectedColumns.length
            ? 'You must select atleast one field to apply bulk update.'
            : 'You must select atleast one video for bulk update.',
        });
      }, 50);
      setFormSubmitted(false);
      return;
    }

    selectedRows.forEach((videoId) => {
      selectedColumns.forEach((field) => {
        childRef.current.setChildValue(
          `cellvalue-${videoId}-${field}`,
          data[`cellvalue_${field}`]
        );
      });
    });

    bulkEditInitialValues.current = getValues();
    setFormSubmitted(false);
  };

  const onInlineEditSuccess = () => {
    setSelectedRows([]);
    setSelectedColumns([]);
    // reset();
    handleRefreshData();
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    setSnackbarInfo({ ...snackbarInfo, show: false });

    if (selectedAction === ACTIONS.PLAY_VIDEO.value) {
      const url = replaceIdUrl(ROUTE_PATH.VIDEO_PREVIEW, selectedData.id);
      const popup = window.open(url, '_blank', 'width=720,height=405');
      if (!popup) {
        setTimeout(() => {
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'warning',
            message: 'Popup blocked! Please allow popups for this site.',
          });
        }, 50);
      }
    }
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };

  const handleCancel = () => {
    if (checkUnsavedChanges()) {
      setShowConfirmDialog(true);
    } else {
      onCancel();
    }
  };

  const handleConfirmDiscard = () => {
    setShowConfirmDialog(false);
    onCancel();
  };

  const handleCancelDiscard = () => {
    setShowConfirmDialog(false);
  };

  useUnsavedChanges(checkUnsavedChanges);

  useEffect(() => {
    const signal = getSignal();
    setOptions({ ...options, loading: true });

    const params = [];
    params.push(`page=${options.page + 1}`);
    params.push(`perPage=${options.rowsPerPage}`);
    if (options.sortBy && options.sortOrder) {
      params.push(`sortBy=${options.sortBy}`);
      params.push(`sortOrder=${options.sortOrder}`);
    }
    if (selectedWorkspace?.id) {
      params.push(`workspaceId=${selectedWorkspace.id}`);
    }

    const filters = Object.entries(options.appliedFilters)
      .filter(([, filter]) => filter.apply)
      .map(([field, filter]) => ({
        field,
        ...filter,
      }));
    if (filters.search) {
      params.push(`q=${encodeURIComponent(filters.search)}`);
    }

    const uri = '';
    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    getListData(uri + paramsQuery, { filter: filters, columnFields }, signal)
      .then((res) => {
        if (!signal.aborted) {
          if (columnFields.length === 0) {
            const defaultFields = res?.data?.columnsFields
              .filter((item) => item.id)
              .map((item) => item.fieldName);
            setColumnFields(defaultFields);
          }

          let columns = [];
          if (selectedWorkspace?.id) {
            columns = prepareDynamicColumns(res?.data?.columnsFields);
          } else if (columnFields?.length > 0) {
            columns = [
              ...DEFAULT_LIST_COLUMNS,
              ...prepareDynamicColumns(res?.data?.columnsFields).filter(
                (col) => col.dataKey !== 'title'
              ),
            ];
          } else {
            columns = DEFAULT_LIST_COLUMNS;
          }

          setOptions({
            ...options,
            loading: false,
            totalRows: res?.data?.totalRows || 0,
            rows: res?.data?.rows || [],
            columns,
            error: false,
          });
        }
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        setOptions({
          ...options,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          error: true,
        });
      });
  }, [options.reloadCounter, selectedWorkspace?.id]);

  useEffect(() => {
    bulkEditInitialValues.current = getValues();
  }, [options.columns]);

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          minWidth: '95vw',
          minHeight: '95vh',
          maxWidth: '95vw',
          maxHeight: '95vh',
        },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="calc(100% - 300px)"
      >
        <DialogTitle id="add-dialog-title">{title}</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          size="small"
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Iconify icon="ic:outline-close" />
        </IconButton>
      </Box>

      <DialogContent
        dividers
        id="add-dialog-description"
        sx={{ width: 'calc(100% - 300px)' }}
      >
        <Box>
          <Box flex={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ py: 2 }}
            >
              <Box width="70%" display="flex" alignItems="center">
                <Workspace
                  id="workspace"
                  name="workspace"
                  label="Workspace"
                  defaultValue={selectedWorkspace}
                  onChange={(v) => {
                    setSelectedWorkspace(v);
                    setSelectedColumns([]);
                    setSelectedRows([]);
                    setColumnFields([]);
                  }}
                  sx={{ width: '40%', mr: 2 }}
                />
                <Box width="40%">
                  <FieldChooser
                    id="field_chooser"
                    name="field_chooser"
                    label="Field Chooser"
                    defaultValue={columnFields}
                    sx={{ width: '100%' }}
                    onFieldClick={onFieldClick}
                    checkUnsavedChanges={checkUnsavedChanges}
                    updateUnsavedChanges={(v) =>
                      setShowApplyFilterConfirmDialog(v)
                    }
                  />
                </Box>
              </Box>
              <Button
                color="inherit"
                variant="contained"
                startIcon={<Iconify icon="ic:twotone-refresh" />}
                onClick={handleRefreshData}
                disabled={options.loading}
              >
                Refresh
              </Button>
            </Stack>

            {options.appliedFilters &&
              Object.values(options.appliedFilters).some(
                (filter) =>
                  filter.apply &&
                  ((filter.type === 'checkbox' && filter.value) ||
                    filter.type !== 'checkbox')
              ) && (
                <Box mb={2}>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    display="inline"
                  >
                    Filters Applied:{' '}
                    <Chip
                      key="all"
                      label="Clear all"
                      onDelete={() => handleDeleteFilters('clear-all')}
                      sx={{ ml: 1, color: 'red' }}
                    />
                  </Typography>

                  {Object.entries(options.appliedFilters)
                    .filter(
                      ([, filter]) =>
                        filter.apply &&
                        ((filter.type === 'checkbox' && filter.value) ||
                          filter.type !== 'checkbox')
                    )
                    .map(([field, filter]) => (
                      <Chip
                        key={field}
                        label={filter.title || 'Active'}
                        onDelete={() => handleDeleteFilters(field)}
                        sx={{ ml: 1 }}
                      />
                    ))}
                </Box>
              )}

            <form
              id="bulk-edit-form"
              onSubmit={handleSubmit(onFormSubmit)}
              style={{ marginBottom: 16 }}
            >
              <Accordion
                key="bulk-edit-metadata"
                expanded={expandBulkEdit}
                onChange={() => setExpandBulkEdit(!expandBulkEdit)}
                variant="outlined"
              >
                <AccordionSummary
                  expandIcon={
                    <Iconify icon="ic:round-expand-more" width={25} />
                  }
                >
                  <Typography variant="subtitle1">
                    Bulk Edit Video Metadata
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        {!options.columns.filter((item) => item.editable)
                          .length && (
                          <TableRow>
                            <TableCell align="center">
                              <Typography variant="body2">
                                No data found
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        {options.columns.filter((item) => item.editable)
                          .length > 0 && (
                          <TableRow>
                            {options.columns
                              .filter((item) => item.editable)
                              .map((c) => (
                                <TableCell
                                  key={`col-${c.id}`}
                                  align={c.align}
                                  width={c.width}
                                  sx={{
                                    cursor: c.sortable ? 'pointer' : 'inherit',
                                    padding: '16px',
                                    fontSize: '0.875rem !important',
                                  }}
                                >
                                  <Checkbox
                                    checked={selectedColumns.includes(c.id)}
                                    onChange={() => {
                                      setSelectedColumns((prevSelected) =>
                                        prevSelected.includes(c.id)
                                          ? prevSelected.filter(
                                              (colId) => colId !== c.id
                                            )
                                          : [...prevSelected, c.id]
                                      );
                                    }}
                                  />
                                  {c.label}
                                </TableCell>
                              ))}
                          </TableRow>
                        )}
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          {options.columns
                            .filter((item) => item.editable)
                            .map((c) => (
                              <TableCell
                                key={`data-col-${c.id}`}
                                align={c.align}
                                width={c.width}
                                sx={{
                                  padding: '16px',
                                  fontSize: '0.875rem !important',
                                }}
                                variant="string"
                              >
                                <Controller
                                  name={`cellvalue_${c.dataKey}`}
                                  control={control}
                                  defaultValue={getEditableCellDefaultValue(
                                    c.dataKey,
                                    DYNAMIC_COLUMNS_CONFIG[c.dataKey]
                                      .editCellType
                                  )}
                                  render={({ field: { onChange, value } }) => (
                                    <EditableCellValue
                                      type={
                                        DYNAMIC_COLUMNS_CONFIG[c.dataKey]
                                          .editCellType
                                      }
                                      dataKey={c.dataKey}
                                      value={value}
                                      onChange={onChange}
                                    />
                                  )}
                                />
                              </TableCell>
                            ))}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
                <AccordionActions sx={{ justifyContent: 'flex-start', px: 2 }}>
                  {hasOperationAccess('video_library', 'edit') && (
                    <Button
                      type="submit"
                      form="bulk-edit-form"
                      color="primary"
                      variant="contained"
                      disabled={formSubmitted}
                    >
                      Apply Bulk
                    </Button>
                  )}
                </AccordionActions>
              </Accordion>
            </form>

            <Card>
              <ListVideosData
                ref={childRef}
                columns={options.columns}
                rows={options.rows}
                page={options.page}
                rowsPerPage={options.rowsPerPage}
                totalRows={options.totalRows}
                loading={options.loading}
                actions={[
                  {
                    action: ACTIONS.PLAY_VIDEO.value,
                    label: ACTIONS.PLAY_VIDEO.label,
                    conditionalDisabled: (val) => !val.video_url,
                  },
                  {
                    action: ACTIONS.VIEW.value,
                    label: ACTIONS.VIEW.label,
                    conditional: (val) => val === 'XXXX',
                  },
                ]}
                error={options.error}
                sortBy={options.sortBy}
                sortOrder={options.sortOrder}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onSortChange={handleSortingChange}
                fromDailog
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                allowInlineEdit
                onInlineEditSuccess={onInlineEditSuccess}
                onAction={handleActionSelect}
                showNumberedList
                isEditMode={isEditMode}
              />
            </Card>
          </Box>

          <Drawer
            anchor="right"
            open
            variant="persistent"
            PaperProps={{
              sx: {
                width: 300,
                overflow: 'auto',
                height: '95%',
                top: '2.5%',
                right: '2.5%',
                borderTopRightRadius: 6,
                borderBottomRightRadius: 6,
              },
            }}
          >
            {hasContentOwnerAccess() ? (
              <DistributorFilterVideos
                values={options.appliedFilters}
                onSuccess={handleFilterApply}
                onClear={handleClearFilters}
                from="edit-videos-list"
              />
            ) : (
              <FilterVideos
                values={options.appliedFilters}
                onSuccess={handleFilterApply}
                onClear={handleClearFilters}
                from="edit-videos-list"
              />
            )}
          </Drawer>
        </Box>
      </DialogContent>

      <Box width="calc(100% - 300px)">
        <StyledDialogActions>
          <Grid>
            {hasOperationAccess('video_library', 'edit') && (
              <Button
                type="submit"
                form="inline-edit-form"
                color="primary"
                variant="contained"
                disabled={formSubmitted}
                sx={{ marginRight: 2 }}
              >
                Save
              </Button>
            )}
            <Button
              color="secondary"
              variant="contained"
              onClick={handleCancel}
              disabled={formSubmitted}
            >
              Cancel
            </Button>
          </Grid>
        </StyledDialogActions>
      </Box>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}

      {(showConfirmDialog || showApplyFilterConfirmDialog) && (
        <ConfirmPopup
          title="Unsaved Changes"
          message="You have unsaved changes. Are you sure you want to discard them?"
          onCancel={
            showConfirmDialog
              ? handleCancelDiscard
              : handleApplyFilterConfirmCancel
          }
          onSuccess={
            showConfirmDialog
              ? handleConfirmDiscard
              : handleApplyFilterConfirmSuccess
          }
          successButtonText="Discard"
          cancelButtonText="Stay"
        />
      )}
    </Dialog>
  );
};

EditVideosList.propTypes = {
  title: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  viewModeWorkspace: PropTypes.object.isRequired,
  videoListFilters: PropTypes.object.isRequired,
  initialColumnFields: PropTypes.array.isRequired,
  isEditMode: PropTypes.bool.isRequired,
};

export default EditVideosList;
